import { useState, useEffect, useRef } from 'react';
import { PenTool, Send, Trash2, Plus, MessageSquare, Loader2, Copy, Check, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import api from '../lib/api';
import ReactMarkdown from 'react-markdown';

export default function Writer() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Generate or load session ID
  useEffect(() => {
    const storedSessionId = localStorage.getItem('writerSessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadChatHistory(storedSessionId);
    } else {
      const newSessionId = crypto.randomUUID();
      setSessionId(newSessionId);
      localStorage.setItem('writerSessionId', newSessionId);
    }
    loadSessions();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const response = await api.get('/agents/sessions');
      const writerSessions = response.data.sessions.filter(s => s.agent_type === 'writer');
      setSessions(writerSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadChatHistory = async (sid) => {
    try {
      const response = await api.get(`/agents/writer/history/${sid}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setMessages(response.data.messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp
        })));
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await api.post('/agents/writer/chat', {
        message: userMessage.content,
        session_id: sessionId
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      loadSessions();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const startNewSession = () => {
    const newSessionId = crypto.randomUUID();
    setSessionId(newSessionId);
    setMessages([]);
    localStorage.setItem('writerSessionId', newSessionId);
    toast.success('Started new conversation');
  };

  const switchSession = (sid) => {
    setSessionId(sid);
    localStorage.setItem('writerSessionId', sid);
    loadChatHistory(sid);
  };

  const clearSession = async () => {
    if (!sessionId) return;
    
    try {
      await api.delete(`/agents/writer/session/${sessionId}`);
      setMessages([]);
      startNewSession();
      loadSessions();
      toast.success('Conversation cleared');
    } catch (error) {
      console.error('Error clearing session:', error);
      toast.error('Failed to clear conversation');
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    toast.success('Copied to clipboard');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedPrompts = [
    "Write a grant proposal for a community health initiative",
    "Help me draft a cover letter for a partnership opportunity",
    "Create an outreach email to potential foundation donors",
    "Write a technical project description for a software development grant"
  ];

  return (
    <div className="space-y-6 h-[calc(100vh-120px)] flex flex-col" data-testid="writer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-theme-accent/20 flex items-center justify-center">
            <PenTool className="w-7 h-7 text-theme-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-theme-light font-mono">Writer Agent</h1>
            <p className="text-theme-muted text-sm">Grant proposals, cover letters & documentation</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={startNewSession}
            className="border-theme-accent text-theme-accent hover:bg-theme-accent/10"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Chat
          </Button>
          <Button
            variant="outline"
            onClick={clearSession}
            className="border-theme-danger text-theme-danger hover:bg-theme-danger/10"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sessions Sidebar */}
        <div className="w-64 bg-theme-darker rounded-xl border border-theme-accent/20 p-4 hidden lg:block">
          <h3 className="text-theme-secondary font-mono text-sm mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Recent Sessions
          </h3>
          <ScrollArea className="h-[calc(100%-2rem)]">
            <div className="space-y-2">
              {sessions.length === 0 ? (
                <p className="text-theme-muted text-xs">No previous sessions</p>
              ) : (
                sessions.map((session, idx) => (
                  <button
                    key={idx}
                    onClick={() => switchSession(session.session_id)}
                    className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                      sessionId === session.session_id
                        ? 'bg-theme-accent/20 text-theme-light'
                        : 'hover:bg-theme-dark text-theme-muted'
                    }`}
                  >
                    <div className="truncate">{session.last_message || 'New conversation'}</div>
                    <div className="text-theme-muted text-[10px] mt-1">
                      {session.message_count} messages
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-theme-darker rounded-xl border border-theme-accent/20">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <div className="w-20 h-20 rounded-full bg-theme-accent/10 flex items-center justify-center mb-4">
                  <FileText className="w-10 h-10 text-theme-accent" />
                </div>
                <h2 className="text-xl font-bold text-theme-light mb-2">Professional Writing Assistant</h2>
                <p className="text-theme-muted max-w-md mb-6">
                  I can help you write grant proposals, cover letters, outreach emails, and technical documentation. 
                  Tell me what you need to write!
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl">
                  {suggestedPrompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInputMessage(prompt)}
                      className="text-left p-3 rounded-lg bg-theme-dark hover:bg-theme-accent/10 text-theme-muted hover:text-theme-light text-sm transition-colors border border-theme-accent/10"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message, idx) => (
                  <div
                    key={idx}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-xl p-4 ${
                        message.role === 'user'
                          ? 'bg-theme-accent text-white'
                          : 'bg-theme-dark text-theme-light'
                      }`}
                    >
                      {message.role === 'assistant' ? (
                        <div className="prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p>{message.content}</p>
                      )}
                      {message.role === 'assistant' && (
                        <button
                          onClick={() => copyToClipboard(message.content, idx)}
                          className="mt-2 text-theme-muted hover:text-theme-light transition-colors"
                        >
                          {copiedIndex === idx ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-theme-dark rounded-xl p-4 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-theme-accent" />
                      <span className="text-theme-muted">Writing...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4 border-t border-theme-accent/20">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Describe what you need to write..."
                className="flex-1 bg-theme-dark border-theme-accent/30 text-theme-light placeholder:text-theme-muted"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="bg-theme-accent hover:bg-theme-accent/80 text-white"
                data-testid="send-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
